import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Bell, Check, Heart, UserPlus, Copy, X, Star, Trophy, Medal, UtensilsCrossed, CalendarClock } from "lucide-react";
import { GustoIcon } from "@/components/GustoLogo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import ChefAvatar from "@/components/ChefAvatar";

const NotificationBell = () => {
  const { token, user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch unread count periodically
  useEffect(() => {
    if (!token) return;

    const fetchUnreadCount = async () => {
      try {
        const res = await axios.get(`${API}/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadCount(res.data.count);
      } catch (error) {
        console.error("Error fetching unread count:", error);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [token]);

  const fetchNotifications = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(
        `${API}/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(
        `${API}/notifications/read-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success("Toutes les notifications lues");
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "new_recipe":
        return <UtensilsCrossed className="w-4 h-4 text-primary" />;
      case "new_follower":
      case "new_friend":
        return <UserPlus className="w-4 h-4 text-secondary" />;
      case "recipe_liked":
        return <Heart className="w-4 h-4 text-red-500 fill-red-500" />;
      case "recipe_reproduced":
        return <Copy className="w-4 h-4 text-accent" />;
      case "meal_rated":
        return <Star className="w-4 h-4 text-amber-500 fill-amber-500" />;
      case "meal_invite":
        return <UtensilsCrossed className="w-4 h-4 text-primary" />;
      case "meal_rsvp":
        return <Check className="w-4 h-4 text-green-500" />;
      case "meal_reminder":
        return <CalendarClock className="w-4 h-4 text-orange-500" />;
      case "meal_rate_invite":
        return <Star className="w-4 h-4 text-amber-500" />;
      case "order_confirmed":
        return <UtensilsCrossed className="w-4 h-4 text-green-600" />;
      case "level_up":
        return <Trophy className="w-4 h-4 text-amber-600" />;
      case "points_earned":
        return <Medal className="w-4 h-4 text-primary" />;
      default:
        return <Bell className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={handleBellClick}
        className="relative rounded-full hover:bg-primary/10"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-charcoal" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-12 w-80 sm:w-96 z-50"
          >
            <Card className="shadow-xl border-0 overflow-hidden">
              {/* Header */}
              <div className="px-4 py-3 bg-gradient-to-r from-primary/10 to-accent/10 border-b flex items-center justify-between">
                <h3 className="font-heading font-semibold text-charcoal flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  Notifications
                  {unreadCount > 0 && (
                    <Badge className="bg-red-500 text-white text-xs">{unreadCount}</Badge>
                  )}
                </h3>
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={markAllAsRead}
                    className="text-xs text-primary hover:text-primary/80"
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Tout lire
                  </Button>
                )}
              </div>

              {/* Notifications List */}
              <ScrollArea className="max-h-[400px]">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  </div>
                ) : notifications.filter(n => !n.read).length === 0 ? (
                  <div className="p-8 text-center">
                    <Bell className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                    <p className="text-muted-foreground text-sm">Aucune notification non lue</p>
                  </div>
                ) : (
                  <div>
                    {notifications.filter(n => !n.read).map((notification) => (
                      <Link
                        key={notification.id}
                        to={notification.link || "#"}
                        onClick={() => {
                          markAsRead(notification.id);
                          setIsOpen(false);
                        }}
                      >
                        <div
                          className="px-4 py-3 border-b hover:bg-muted/50 transition-colors bg-primary/5"
                        >
                          <div className="flex gap-3">
                            {/* Avatar or Icon */}
                            <div className="flex-shrink-0">
                              {notification.from_user_avatar ? (
                                <ChefAvatar
                                  src={notification.from_user_avatar}
                                  name={notification.from_user_pseudo}
                                  size="sm"
                                  showToque={false}
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                                  {getNotificationIcon(notification.type)}
                                </div>
                              )}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">
                                {notification.message}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatTime(notification.created_at)}
                              </p>
                            </div>

                            {/* Unread indicator */}
                            <div className="flex-shrink-0">
                              <div className="w-2 h-2 bg-primary rounded-full"></div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </ScrollArea>

              {/* See all link */}
              <div className="border-t px-4 py-2">
                <Link
                  to="/notifications"
                  onClick={() => setIsOpen(false)}
                  className="block text-center text-sm text-primary hover:text-primary/80 font-medium py-1"
                  data-testid="see-all-notifications"
                >
                  Voir toutes les notifications
                </Link>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
